<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：通知送信ダイアログ
 *
 * 作成者：2021/12/2 ノア）黄
 * 最終修正者：2022/2/6 ノア）黄
 *
 -->
<template>
  <div>
    <!-- 通知送信ダイアログ -->
    <ttiDialog
      :isShow="isShow"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('notifications.notificationsRegist')"
      width="450px"
    >
      <template slot="body">
        <el-form
          ref="notificationsSendFormRef"
          :model="notificationsSendForm"
          :rules="notificationsSendRules"
          label-width="90px"
        >
          <!-- 通知題名 -->
          <el-form-item prop="title" :label="$t('notifications.notificationsTitle')">
            <el-input v-model.trim="notificationsSendForm.title" size="small"></el-input>
          </el-form-item>
          <!-- 通知内容 -->
          <el-form-item prop="context" :label="$t('notifications.notificationsContext')">
            <el-input type="textarea" v-model.trim="notificationsSendForm.context" size="small"></el-input>
          </el-form-item>
          <!-- 通知種類 -->
          <el-form-item prop="sendType" :label="$t('notifications.notificationsType')">
            <el-select v-model="notificationsSendForm.sendType" size="small">
              <el-option
                v-for="item in notificationsTypeOptions"
                :key="item.code"
                :label="item.name"
                :value="item.code"
              >
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </template>
      <template slot="foot">
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onRegistBtnClick"
          v-tooltip="getTooltip(tooltipType.notificationSend)"
        >
          {{ $t('buttons.regist') }}</el-button
        >
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Ref, Watch } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { AxiosResponse } from 'axios'

import TtiDialog from '@/components/TtiDialog.vue'
import { dialog } from '@/store/modules'
import { NotificationsSend, General, ResponseData } from '@/models'
import manualConfig from '@/config/manualConfig'
import { i18n } from '@/locales'
import { AnnounceType } from '@/commons/constants'
import { announceSend, postLogout } from '@/store/apis'
import { RULES, validateService } from '@/service/validateService'
import { generalService, GeneralType } from '@/commons/services/generalService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { moveService } from '@/commons/services/moveService'
import { clearLocal, clearSession } from '@/commons/services/localService'
import HelperService from '@/service/helperService'
import config from '@/config/config'
import { clearWindowCookie } from '@/service/refreshAndCloseWindowService'

@Component({
  name: 'home-notifications-send',
  components: {
    TtiDialog
  }
})
export default class HOME_NOTIFICATIONS_SEND extends Vue {
  /**
   * ツールチップタイプ
   */
  tooltipType = {
    // 登録
    notificationSend: 'notificationSend'
  }

  /**
   * 通知送信サブ画面の表示フラグを取得
   *
   * @returns {boolean} 通知送信サブ画面の表示フラグ
   */
  get isShow(): boolean {
    return dialog.isNotificationsSendDialogShow
  }

  /**
   * 通知送信サブ画面の表示フラグを設定する
   *
   * @param {boolean} value 通知送信サブ画面の表示フラグ
   */
  set isShow(value: boolean) {
    dialog.setNotificationsSendDialogShow(value)
  }

  /**
   * 通知送信サブ画面のマニュアルキーを取得
   *
   * @returns {string} 通知送信サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.notificationsSend
  }

  /**
   * 通知送信フォームのref
   */
  @Ref() readonly notificationsSendFormRef!: Form

  /**
   * 通知送信Form
   */
  notificationsSendForm: NotificationsSend = {
    title: '',
    context: '',
    sendType: AnnounceType.COMPANY
  }

  /**
   * 通知対象リスト
   */
  notificationsTypeOptions: General[] = []

  /**
   * 通知送信検証ルール
   */
  notificationsSendRules: Form['rules'] = {}

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('HOME_NOTIFICATIONS_SEND > mounted')
    this.fetchMasterData()
  }

  @Watch('isShow')
  onIsShowChange(value: boolean): void {
    this.$log.debug('HOME_NOTIFICATIONS_SEND > onIsShowChange', value)
    if (value) {
      this.initForm()
      this.initRule()
      this.initValidate(this.notificationsSendRules, this.notificationsSendFormRef)
    }
  }

  /**
   * FORMを初期化する
   *
   * @returns {void}
   */
  initForm(): void {
    this.notificationsSendForm = {
      title: '',
      context: '',
      sendType: AnnounceType.COMPANY
    }
  }

  /**
   * メッセージ送信検証ルール
   */
  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.notificationsSendRules = {
      // 通知題名
      title: [
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('notifications.notificationsTitle')]).toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 29文字
            {
              rule: RULES.MAXLENGTH,
              number: 29,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('notifications.notificationsTitle'), 29]).toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 通知内容
      context: [
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('notifications.notificationsContext')]).toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 120文字
            {
              rule: RULES.MAXLENGTH,
              number: 120,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('notifications.notificationsContext'), 120])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * 定義ルールを初期化する
   *
   * @param {any} allRules 定義ルール
   * @param {Form} form フォーム
   * @returns {void}
   */
  // eslint-disable-next-line
  initValidate(allRules: any, form: Form): void {
    if (!allRules) {
      return
    }
    Object.values(allRules).forEach(rules => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      for (const rule of rules) {
        if (!rule.validRule) {
          continue
        }

        rule.validator = validateService.getValidator(rule.validRule)
      }
    })

    if (form) {
      this.$nextTick(() => {
        form.clearValidate()
      })
    }
  }

  /**
   * 汎用定義通知対象を取得する
   *
   * @returns {void}
   */
  async fetchMasterData(): Promise<void> {
    try {
      // 通知対象
      const options = await generalService[GeneralType.ANNOUNCE_TYPE]
      this.notificationsTypeOptions = options.filter(item => {
        return item.code === AnnounceType.COMPANY || item.code === AnnounceType.BUSINESS_OFFICE
      })
    } catch (e) {
      this.$log.debug('HOME_NOTIFICATIONS_SEND > fetchMasterData: e=', e)
      throw e
    }
  }

  /**
   * 表示状態変更
   *
   * @param {boolean} value 表示状態
   * @returns {void}
   */
  onHandelDisplay(value: boolean): void {
    dialog.setNotificationsSendDialogShow(value)
  }

  /**
   * 登録ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onRegistBtnClick(): Promise<void> {
    this.$log.debug('HOME_NOTIFICATIONS_SEND > onRegistBtnClick')
    const valid = await this.notificationsSendFormRef.validate()
    if (!valid) {
      return
    }

    try {
      await announceSend(this.notificationsSendForm)
      msgBoxService.info(i18n.t('notifications.I00070').toString())
    } catch (e) {
      this.$log.debug('HOME_NOTIFICATIONS_SEND > onRegistBtnClick: e=', e)
      const err = e as AxiosResponse<ResponseData>
      //ログインユーザの営業所が削除された場合、アカウントをログアウトし、ログイン画面へ遷移する。
      if (err.data.common.code === 'E00064') {
        await moveService.beforeLogout()
        this.doLogout()
      }
    }
  }

  /**
   * ログアウトを実行する。
   *
   * @returns {void}
   */
  async doLogout(): Promise<void> {
    try {
      await postLogout()
      clearLocal()
      clearSession()
      HelperService.removeAccessToken()
      clearWindowCookie()
      Vue.nextTick(() => {
        this.$router.push(config.menu.login)
      })
    } catch (e) {
      Vue.$log.debug('Login > onLogout: e=', e)
      throw e
    }
  }

  /**
   * 画面ツールチップを取得する
   *
   * @param {string} key ツールチップキー
   * @returns {any} ツールチップ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTooltip(key: string): any {
    return {
      content: i18n.t(`master.tooltipCommon.${key}`),
      placement: 'bottom-start',
      delay: { show: 1000, hide: 0 }
    }
  }
}
</script>
